import React, { useMemo } from "react";
import { Box, Button, Card, Chip, Grid, LinearProgress, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

const items = [
    { label: "DM", count: 6, color: "#FF383C" },
    { label: "GRIP", count: 10, color: "#FF9800" },
    { label: "GRIL", count: 2, color: "#1268AD" },
];

const usedColors = new Set();

const generateFallbackColor = (text) => {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
        hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }

    const goldenAngle = 137.508;

    // Allowed hue ranges (blue → purple only)
    const ranges = [
        [180, 240], // cyan-blue
        [260, 320], // purple-magenta
    ];

    let attempt = 0;
    let hex;

    do {
        const range = ranges[Math.abs(hash + attempt) % ranges.length];
        const hue =
            range[0] +
            ((Math.abs(hash) + attempt * goldenAngle) %
                (range[1] - range[0]));

        const saturation = 65;
        const lightness = 50;

        hex = hslToHex(hue, saturation, lightness);
        attempt++;
    } while (usedColors.has(hex) && attempt < 10);

    usedColors.add(hex);
    return hex;
};


const hslToHex = (h, s, l) => {
    s /= 100;
    l /= 100;

    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) =>
        l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

    return (
        "#" +
        [f(0), f(8), f(4)]
            .map((x) =>
                Math.round(255 * x)
                    .toString(16)
                    .padStart(2, "0")
            )
            .join("")
    );
};


const OWNER_COLOR_MAP: Record<string, string> = {
    'DM': "#FF383C",
    'GRIP': "#FF9800",
    "GRIL": "#1268AD"
};

const ActiveRiskByOwners = ({ data }) => {
    const percentage = 18; // dynamic value

    const chartData = data?.data?.owners?.map(item => ({
        y: item?.count,              // or item.percentage if required
        name: item?.owner,
        color: generateFallbackColor(item?.owner), // fallback
    }));

    const options = {
        chart: {
            type: "pie",
            height: 250,
            backgroundColor: "transparent",
            margin: 0
        },

        title: {
            text: ``,
            align: "center",
            verticalAlign: "middle",
            useHTML: true,
            y: 10
        },

        tooltip: {
            enabled: true
        },

        plotOptions: {
            pie: {
                innerSize: "50%",
                size: '100%',
                // minSize:60,
                borderWidth: 3,
                borderRadius: 5,
                borderColor: "#fff",
                dataLabels: {
                    enabled: false
                }
            }
        },

        series: [
            {
                name: "Active Risk By Owners",
                data: chartData
            },
        ],

        credits: {
            enabled: false
        },

        legend: {
            enabled: false
        }
    };

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                border: "1px solid #E4E4E4",
                width: '100%',
            }}
        >
            <Box sx={{
                width: '100%',
                borderBottom: "1px solid #E4E4E4",
                p: 1.5,
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <Box>
                    <Typography variant="subtitle1" sx={{
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 600,
                        fontSize: '20px',
                        letterSpacing: "0px",
                        mb: 0
                    }}>
                        Active Risk by Owners
                    </Typography>
                    <Typography variant="body2" sx={{
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 300,
                        fontSize: '16px',
                        letterSpacing: "0px",
                        color: '#7D7F81'
                    }}>
                        Shows how active risks are distributed among owners to clarify accountability.
                    </Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid size={{ md: 6 }}>
                    <Stack spacing={3} sx={{ pt: 5, pl: 1.5, pb: 1.5, maxHeight: "258px", minHeight: '258px', overflowY: "auto" }} justifyContent={"center"}>
                        {chartData?.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    border: "1px solid #EAEAEA",
                                    borderRadius: "4px",
                                    padding: "8px 10px",
                                    backgroundColor: "#fff",
                                }}
                            >
                                {/* Left Section */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: "50%",
                                            backgroundColor: item.color,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: "16px",
                                            fontWeight: 400,
                                            color: "#010814",
                                            fontFamily: "Outfit, sans-serif",
                                            lineHeight: '18px',
                                            verticalAlign: 'middle'
                                        }}
                                    >
                                        {item?.name}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        minWidth: 40,
                                        height: 18,
                                        borderRadius: "5px",
                                        backgroundColor: "#E7E7EB",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#433F5E",
                                        fontFamily: "Outfit, sans-serif",
                                    }}
                                >
                                    {String(item?.y).padStart(2, "0")}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Grid>
                <Grid size={{ md: 6 }}>
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "flex-end",
                        }}
                    >
                        <HighchartsReact highcharts={Highcharts} options={options} />
                    </Box>
                </Grid>
            </Grid>
        </Card>
    );
};

export default ActiveRiskByOwners;
